{ /* Generated by Copilot- Start - 2026-02-23 16:38:41 */ }
import React, { useState, useDeferredValue, useCallback, useMemo, useRef, useEffect } from "react";
import DtvBox from "@directv-digital/dds.ui.dtv-box";
import TextField from "@mui/material/TextField";
import { MagnifyingGlassIcon, CloseIcon } from "@directv-digital/fusion.atoms.icons";
import DtvTheme from "@directv-digital/dds.ui.dtv-theme";
import { MemoizedDropdownList } from "./DropdownList";
import { MemoizedFilterChips } from "./FilterChips";
import { useClickOutside } from "src/hooks/useClickOutside";
import Link from "@mui/material/Link";
import { visuallyHidden } from "@mui/utils";
import {
  fireDataLayerEvent,
  fireSearchImpressions
} from "@directv-digital/dds.services.analytics-service";
import RadioButton from "@directv-digital/fusion.atoms.radio-button";
import { fetchOVMultisearchData } from "./utils/MultisearchService";
import { getChoiceKey, getChoiceLabel } from "./utils/choice-key";
import { useLocationHref } from "src/hooks/useLocationHref";
// Generated by Copilot- Start - 2026-03-25 19:46:05
/** Discriminates the caller context so onClearFilter can adjust side-effects
 *  (e.g. skip re-focusing the input when the clear was triggered by a zipcode change). */
type ClearFilterType = 'user' | 'zipcode';

// Generated by Copilot- End - 2026-03-25 19:46:05

interface ChannelSearchProps {
    config: {
        isReset?: boolean;
        showChips?: boolean;
    },
    theming?: "dark" | "light";
    categoryData?: any;
    channels?: any[];
    updateSearchEvents?: (searchChannels: any[]) => void;
    type?: string;
    id?: string;
    searchLabel?: {
        content: string;
    };
    radioOptions?: any;
    singlePackageView?: boolean;
    enableRadioButton?: boolean;
    isFFPMultiSearch?: boolean;
    maxSelections?: number;
    // Generated by Copilot- Start - 2026-03-24 01:31:28
    zipcodeChanged?: boolean;
    setZipcodeChanged?: (changed: boolean) => void;
    // Generated by Copilot- End - 2026-03-24 01:31:28
    /**
     * WAT-12718 — the GTM `linkPosition` every click target inside this search
     * reports (reset control, suggestion rows, chips). Defaults to the panel
     * placement the standalone channel-lineup page and the other hosts already
     * send; the modal (`channel-compare-tool-v3`) passes `modal_channel lineup`
     * so its clicks file under the modal placement instead.
     */
    linkPosition?: string;
    /**
     * WAT-12718 AC3 — when BOTH are set, a suggestion/select-all click pushes the
     * ticket-shaped `search` event (`searchCategory` + `searchSource`) in place of
     * the default `fireSearchImpressions` (`searchCategory: 'channelLineup'`, no
     * source). The modal sets `search component` / `search-channel lineup`; every
     * other host leaves them unset and keeps the existing impression.
     */
    searchCategory?: string;
    searchSource?: string;
}
// Static styles for the inner <input> element (no state dependencies)
const INPUT_ELEMENT_STYLE = {
  height: "24px",
  padding: "22px 0px 18px 16px",
  fontSize: "20px",
  fontFamily: "'Roboto', sans-serif",
  fontWeight: 400,
  lineHeight: 1.3,
  color: "#121212"
} as const;

export const ChannelSearch = (props: ChannelSearchProps) => {
  const { theming, categoryData, type: cmpType } = props;
  const locationHref = useLocationHref();
  // WAT-12718 — the placement every click target here reports. Panel by default
  // (standalone page + other hosts); the modal passes `modal_channel lineup`.
  const linkPosition = props?.linkPosition || "panel_channel lineup";
  // WAT-12718 AC3 — a chosen suggestion fires one `search` event. When the host
  // names a category + source (the modal does), push the ticket's shape; else
  // keep the existing `fireSearchImpressions` (`channelLineup`, no source).
  const fireResultSearch = useCallback((term: string) => {
    const searchTerm = (term || "").toLowerCase();
    if (props?.searchCategory && props?.searchSource) {
      fireDataLayerEvent({
        event: "search",
        searchTerm,
        searchCategory: props.searchCategory,
        searchSource: props.searchSource
      });
      return;
    }
    fireSearchImpressions(searchTerm);
  }, [props?.searchCategory, props?.searchSource]);
  const OV_MULTISEARCH_CATEGORY = "Kids,Movie,Show";
  const OV_DEBOUNCE_DELAY = 300; // ms

  const [searchText, setSearchText] = useState("")
  const deferredSearchText = useDeferredValue(searchText);
  const [dropdownCanShow, setDropdownCanShow] = useState(false);
  const [selectedChannels, setSelectedChannels] = useState([]);
  const [defaultSortOption, setDefaultSortOption] = useState(true);
  const [ovMovieData, setOvMovieData] = useState<any>({ resources: [] });
  const [isOVLoading, setIsOVLoading] = useState<boolean>(false);

  // [WAT-12043] Per-mount listbox id so two ChannelSearch instances on
  // the same page (host tools: ChnlListingPageV2, ChlupInteractiveToolV2,
  // agent PackageCmprGrid) don't share `channel-search-listbox` and
  // misroute the second combobox's aria-controls at the first popup.
  const listboxId = `channel-search-listbox-${React.useId()}`;

  const inputRef = useRef<HTMLInputElement>(null);
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Closes the dropdown and removes focus from the input when the user
  // clicks anywhere outside the component boundary.
  const clickOutsideComponentRef = useClickOutside<HTMLDivElement>(() => {
    setDropdownCanShow(false);
    inputRef?.current?.blur();
  });

  // ─── Radio Button Visibility ──────────────────────────────────────────────
  // The mode-switching radio button is only shown when:
  //   1. The component is used inside the Interactive Tool (cmpType === "interactiveTool").
  //   2. In single-package view, the enableRadioButton prop explicitly opts in.
  //   3. In multi-package view, the radio is shown unless isFFPMultiSearch overrides it.
  const enableRadioButtonData = useMemo(() => {
    if (cmpType !== "interactiveTool") {
      return false;
    }
    if (props?.singlePackageView) {
      return !!props?.enableRadioButton;
    }
    return !props?.isFFPMultiSearch;
  }, [
    cmpType,
    props?.singlePackageView,
    props?.enableRadioButton,
    props?.isFFPMultiSearch
  ]);

  // ─── Channel Mode Filtering ───────────────────────────────────────────────
  // Runs entirely client-side against the `channels` prop array.
  // Splits results into two buckets then merges them so channels whose name
  // STARTS WITH the search term appear before channels that merely INCLUDE it.
  // Returns an empty array when fewer than 2 characters are typed to prevent
  // noisy results for very short queries.
  const filterDropdownData = useMemo(() => {
    if (searchText?.length <= 1 || deferredSearchText?.length <= 1) {
      return [];
    }
    const currentSearchText = deferredSearchText.toUpperCase();
    // [WAT-11775] Read the label through getChoiceLabel (`displayText ?? name`), not
    // `displayText` alone. A SUBSCRIPTION choice carries `name` and no `displayText` — an
    // embeddedSvods entry's fields are name / icon / indicators / chnlPackages — and the
    // compare tool spreads that list in raw. Before WAT-11767 this path THREW on those
    // entries; that PR's optional chaining correctly stopped the crash but turned it into a
    // silent drop, so no subscription was ever offered by the typeahead there. Typing "MA"
    // never surfaced Max.
    const filteredDataStartWith = props?.channels?.filter((choice: any) => getChoiceLabel(choice)?.toUpperCase()?.startsWith(currentSearchText)) ?? [];
    const filteredDataIncludes = props?.channels?.filter((choice: any) => {
      const upper = getChoiceLabel(choice)?.toUpperCase();
      return upper?.includes(currentSearchText) && !upper?.startsWith(currentSearchText);
    }) ?? [];
    const filteredData = [...filteredDataStartWith, ...filteredDataIncludes];
    return filteredData;
  }, [deferredSearchText, searchText, props?.channels]);

  // ─── OV API Call ──────────────────────────────────────────────────────────
  // Sanitises the search string, then calls the server-side catalog proxy.
  const getOVApiData = useCallback(
    async (textToSearch: string) => {
      const sanitized = textToSearch
        .replace(/[^a-zA-Z0-9 %&]/g, "")
        .replace(/%20/g, " ");

      try {
        setIsOVLoading(true);
        const data = await fetchOVMultisearchData(sanitized, OV_MULTISEARCH_CATEGORY);
        setOvMovieData({ resources: data?.resources || [] });
      } catch (_e) {
        setOvMovieData({ resources: [] });
      } finally {
        setIsOVLoading(false);
      }
    },
    []
  );

  // ─── Debounce Wrapper ─────────────────────────────────────────────────────
  // Cancels any pending timer before scheduling a new one so only the API call
  // for the final resting value of deferredSearchText is actually dispatched.
  // This prevents multiple in-flight requests for intermediate keystrokes.
  const debouncedOVSearch = useCallback(
    (textToSearch: string) => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      debounceTimeoutRef.current = setTimeout(() => {
        getOVApiData(textToSearch);
      }, OV_DEBOUNCE_DELAY);
    },
    [getOVApiData]
  );

  // ─── OV Search Trigger Effect ─────────────────────────────────────────────
  // Runs whenever deferredSearchText (or the debounced function reference) changes.
  // - Triggers the debounced OV API call only when the radio button indicates
  //   we are in Shows & Movies mode AND the query is long enough (> 1 char).
  // - Resets OV data and loading state when the user clears the input, ensuring
  //   the dropdown is clean for the next search session.
  useEffect(() => {
    if (enableRadioButtonData && !defaultSortOption && deferredSearchText && deferredSearchText.length > 1) {
      debouncedOVSearch(deferredSearchText as string);
    } else if (!deferredSearchText || deferredSearchText.length <= 1) {
      setOvMovieData({ resources: [] });
      setIsOVLoading(false);
    }
  }, [deferredSearchText, enableRadioButtonData, defaultSortOption, debouncedOVSearch]);
  // ─── Category Data Switcher ───────────────────────────────────────────────
  // Selects the correct category labels/strings for the dropdown header and
  // "Select All" title based on the active search mode.
  // Channel mode  → categoryData.channels
  // OV mode       → categoryData.shows
  const effectiveCategoryData = useMemo(() => {
    if (defaultSortOption) {
      return categoryData?.channels;
    }
    return categoryData?.shows;
  }, [defaultSortOption, categoryData]);

  // ─── Filtered Data Switcher ───────────────────────────────────────────────
  // Channel mode: returns the client-side filtered channels array.
  // OV mode: maps raw API resources into a normalised shape with:
  //   - displayText: primary label shown in the dropdown item.
  //   - category / genre: secondary metadata shown below the title in the
  //     dropdown (rendered as styled text by DropdownList, NOT as HTML strings).
  const effectiveFilteredData = useMemo(() => {
    if (defaultSortOption) {
      return filterDropdownData;
    }
    const movieData = ovMovieData?.resources?.map(resource => {
      const text = resource?.title ? resource?.title :
        resource?.value ? resource?.value?.toString() :
          resource?.name ? resource?.name : "No Suggestion";
      return {
        ...resource,
        displayText: text,
        category: resource?.categories?.[0] || "",
        genre: resource?.genres?.[0] || ""
      };
    }) || [];
    return movieData;
  }, [defaultSortOption, filterDropdownData, ovMovieData]);

  // ─── Dropdown Visibility ──────────────────────────────────────────────────
  // showDrawer: true when there are results to display.
  //   - Channel mode: requires at least one filtered channel.
  //   - OV mode: also stays true while isOVLoading to prevent the dropdown
  //     from flashing closed between a keystroke and the API response.
  const showDrawer = useMemo(() => {
    if (!dropdownCanShow) {
      return false;
    }
    if (defaultSortOption) {
      return searchText?.length > 1 && effectiveFilteredData?.length > 0;
    }
    return (
      searchText?.length > 1 &&
            (isOVLoading || (effectiveFilteredData?.length ?? 0) > 0)
    );
  }, [dropdownCanShow, searchText, effectiveFilteredData, defaultSortOption, isOVLoading]);

  // showDrawerNoMatch: true when we want to show the "no match" or "keep typing"
  // helper message in the dropdown.
  //   - Channel mode: shown immediately when length === 1 or no results.
  //   - OV mode: only shown after loading completes with empty results, so the
  //     user doesn't see a "no match" flash while the API is still in flight.
  const showDrawerNoMatch = useMemo(() => {
    if (!dropdownCanShow) {
      return false;
    }
    if (defaultSortOption) {
      return (
        (searchText?.length === 1) ||
                (searchText?.length > 1 && effectiveFilteredData?.length === 0)
      );
    }
    return (
      searchText?.length > 1 &&
            !isOVLoading &&
            (effectiveFilteredData?.length ?? 0) === 0
    );
  }, [searchText, dropdownCanShow, deferredSearchText, effectiveFilteredData, defaultSortOption, isOVLoading]);
    // Collapses the dropdown and blurs the input; called from keyboard
    // Escape key handlers inside DropdownList.
  const handleCloseDropdown = useCallback(() => {
    setDropdownCanShow(false);
    inputRef?.current?.blur();
  }, []);

  // Merges newly chosen items with the current selection, deduplicating by getChoiceKey to
  // prevent the same channel/show appearing twice in the chip list — without collapsing two
  // DIFFERENT same-named channels into one entry. [WAT-11767/11775] The key is TIERED —
  // feed `matchId`, then OV `resourceId`, then label + channel number — not the name+number
  // pair an earlier version of this comment described.
  const getUniqueChannels = useCallback((choices: any) => {
    const uniqueChannels = [
      ...new Map(
        [...selectedChannels, ...choices].map((item: any) => [getChoiceKey(item), item])
      ).values()
    ];
    return uniqueChannels;
  }, [selectedChannels]);

  const updatEventData = useCallback((sortOption, channels: any[]) => {
    const filtered = enableRadioButtonData ?
      channels.filter((ch: any) => (sortOption ? !ch?.resourceId : !!ch?.resourceId)) :
      channels;
    props?.updateSearchEvents(filtered);
  }, [enableRadioButtonData, defaultSortOption, props?.updateSearchEvents]);

  // Called when the user clicks the "Select All [searchText]" row in the dropdown.
  // Merges all currently visible filtered results into the selection.
  const onMultiSelectTag = useCallback((choices: any) => {
    let uniqueChannels = getUniqueChannels(choices);
    if (props?.maxSelections && uniqueChannels.length > props.maxSelections) {
      uniqueChannels = uniqueChannels.slice(0, props.maxSelections);
    }
    setSelectedChannels(uniqueChannels);
    // WAT-12718 — fall back when the host's feed omits `multiSelectTitle` (the
    // modal's does). `undefined + text` concatenated a literal "undefined" into
    // the search event's searchTerm ("undefinedtest"); the fallback matches the
    // English literal its sibling category strings already default to.
    const text = `${effectiveCategoryData?.multiSelectTitle || "Select all "}${deferredSearchText}`;
    fireResultSearch(text);
    inputRef?.current?.blur();
    setDropdownCanShow(false);
    updatEventData(defaultSortOption, uniqueChannels);
  }, [getUniqueChannels, defaultSortOption, effectiveCategoryData?.multiSelectTitle, deferredSearchText, updatEventData, fireResultSearch]);

  // Called when the user clicks a single item in the dropdown.
  // Toggles the item: removes it from the selection if already selected,
  // or adds it (merged/deduped) if it is new.
  const onSelectTag = useCallback((choice: any) => {
    // [WAT-11767] Toggle on the name + channel-number key, not the name alone:
    // two channels can share a display name, and keying on the name made the
    // second one read as a re-click of the first (removing its chip).
    const choiceKey = getChoiceKey(choice);
    const exists = selectedChannels.some((item: any) => getChoiceKey(item) === choiceKey);
    if (!exists && props?.maxSelections && selectedChannels.length >= props.maxSelections) {
      return;
    }
    const uniqueChannels = exists ?
      selectedChannels.filter((item: any) => getChoiceKey(item) !== choiceKey) :
      getUniqueChannels([choice]);
    setSelectedChannels(uniqueChannels);

    const text = getChoiceLabel(choice) || "No Suggestion";
    // Generated by Copilot- Start - 2026-03-13 14:11:41
    // WAT-12718 AC3 — fire once, on ADD only; a removal (toggle off) sends no search.
    if (!exists) {
      fireResultSearch(text);
    }
    // Generated by Copilot- End - 2026-03-13 14:11:41
    setDropdownCanShow(false);
    inputRef?.current?.blur();
    updatEventData(defaultSortOption, uniqueChannels);
  }, [getUniqueChannels, defaultSortOption, selectedChannels, updatEventData, fireResultSearch]);
    // ─── Input Adornments ─────────────────────────────────────────────────────
    // Static magnifying glass icon placed at the start of the TextField.
  const searchIconAdornment: React.JSX.Element = (<MagnifyingGlassIcon
        fill="#555555"
        style={{
 width: "24px",
height: "24px"
}}
    />);

  // Clear (×) icon shown at the end of the TextField only when there is text.
  // Clicking it calls onClearSearch which respects the isReset prop to decide
  // whether to just clear the input or also reset selections.
  const clearIconAdornment: React.JSX.Element = (deferredSearchText !== '' && <DtvBox
        aria-label="clear icon"
        role="button"
        tabIndex={0}
        sx={{
 width: "24px",
height: "24px",
cursor: "pointer"
}}
        onClick={() => onClearSearch()}
    ><CloseIcon
        style={{
 width: "24px",
height: "24px",
cursor: "pointer"
}}
    /></DtvBox>);

  // Keeps the dropdown open when focus moves to an element inside the component
  // (e.g. a list item) by checking relatedTarget; only closes when focus truly
  // leaves the component.
  const onTextFieldBlur = (e?: any) => {
    if (e?.nativeEvent?.relatedTarget) {
      e.stopPropagation();
    } else {
      setDropdownCanShow(false);
    }
  }

  // Opens the dropdown and focuses the input; used as the TextField onClick.
  const focusField = () => {
    setDropdownCanShow(true);
    inputRef?.current?.focus();
  }

  // isReset=true: only clears the typed text, keeps selections intact.
  // isReset=false: falls through to onClearFilter which also resets selections.
  const onClearSearch = () => {
    if (props?.config?.isReset) {
      setSearchText("");
      setDropdownCanShow(false);
      inputRef?.current?.focus();
    } else {
      onClearFilter('user');
    }
  }
  // Generated by Copilot- Start - 2026-03-24 01:31:28
  useEffect(() => {
    if (props?.zipcodeChanged) {
      onClearFilter('zipcode');
      props?.setZipcodeChanged?.(false);
    }
  }, [props?.zipcodeChanged]);
  // Generated by Copilot- End - 2026-03-24 01:31:28
  // Full reset: clears search text, hides dropdown, and empties selected channels.
  const onClearFilter = (type: ClearFilterType) => {
    setSearchText("");
    setDropdownCanShow(false);
    setSelectedChannels([]);
    if (type !== 'zipcode') {
      inputRef?.current?.focus();
    }
    props.updateSearchEvents?.([]);
  }

  // Whether the search field is in "active" state (focused, has text, or showing dropdown)
  const isActive = dropdownCanShow || searchText?.length > 0 || deferredSearchText?.length > 0;

  return (
       <DtvBox
            id="channel-search-container"
            ref={clickOutsideComponentRef}
            sx={{
                width: "100%",
                position: "relative"
            }}
       >
            <DtvBox
                id="channel-search-input-container"
            >
                {enableRadioButtonData && <DtvBox
                    sx={{
                        '& .MuiFormGroup-root': {
                            display: "flex",
                            flexDirection: "row",
                            flexFlow: "row",
                            width: "100%"
                        },
                        '& .MuiFormGroup-root label:nth-child(2)': {
                            justifyContent: "end"
                        }
                    }}
                >
                    <RadioButton
                        ariaLabel={"Select channels & subscriptions or shows & movies"}
                        id={`${props?.id}-radio-button`}
                        {...{
                            label: "Label",
                            analytics: {
                                tag: "Radiobutton",
                                event: 'Checked',
                                event_name: 'Radiobutton'
                            },
                            mode: `${theming === "dark" ? "dark" : "light"}`,
                            variant: "Radio Label" as any,
                            radiobuttonoptions: props?.radioOptions &&
                            props.radioOptions.map(op => ({
                                label: op?.label,
                                value: op?.key,
                                checked: String(op?.key ?? "") === (defaultSortOption ? "0" : "1"),
                                linkName: op?.label?.toLowerCase?.(),
                                linkPosition,
                                linkDestination: locationHref
                            }))
                        }}
                        onChange={(e: any) => {
                            const { value } = e.target;
                            // Radio options use numeric keys: 0 = Channels, 1 = Shows & Movies.
                            // Both string and number variants are handled since MUI radio
                            // values may be serialised differently across themes.
                            const isChannelMode = value === "0" || value === 0;
                            setDefaultSortOption(isChannelMode);
                            // If the user switches modes while text is already typed,
                            // reopen the dropdown so the correct results show immediately.
                            if (deferredSearchText.length > 1) {
                                setTimeout(() => {
                                    setDropdownCanShow(true);
                                }, 10);
                            }
                            updatEventData(isChannelMode, selectedChannels);
                        }}
                    />
                </DtvBox>}
                {props?.config?.isReset && <DtvBox
                    sx={{
                    width: "100%",
                    margin: "auto",
                    marginBottom: "8px !important",
                    display: "flex",
                    justifyContent: "end"
                    }}
                >
                    {/* Reset search Link */}
                    {/* [WAT-12043 / YFH-AMP116] Removed tabIndex={0} — Link
                        component="button" renders as a native <button>, which
                        is already in the tab order by default. */}
                    <Link
                    aria-label="reset search"
                    component="button"
                    data-linkdestination={locationHref}
                    data-linkposition={linkPosition}
                    id="reset-search-link"
                    rel="noopener"
                    variant="body2medium"
                    sx={{
                        color: theming === "dark" ? "#1FBCF8" : "#2E70BD",
                        fontWeight: 500,
                        textDecoration: "none",
                        fontSize: "15px",
                        marginRight: "4px",
                        [DtvTheme.breakpoints.down(DtvTheme.breakpoints.values.md)]: {
                        fontSize: "14px"
                        },
                        "&:active, &:hover, &:focus": {
                        textDecoration: "underline"
                        }
}}
                    onClick={() => onClearFilter('user')}
                    onKeyDown={event => event?.key === "Enter" && onClearFilter('user')}
                    >
                    Reset Search
                    </Link>
                </DtvBox>}
                <TextField
                    autoComplete="off"
                    disabled={props?.maxSelections > 0 && selectedChannels.length >= props.maxSelections}
                    id="channel-search-input"
                    inputRef={inputRef}
                    label={props?.searchLabel?.content || "Search for Channels"}
                    variant="outlined"
                    InputProps={{
                        startAdornment: searchIconAdornment,
                        endAdornment: clearIconAdornment,
                        id: "multi-select-input",
                        value: searchText,
                        onChange: e => setSearchText(e.target.value),
                        placeholder: "",
                        // [WAT-12043 / YFH-AMP116] Removed tabIndex: 0 —
                        // this landed on the OutlinedInput wrapper. The
                        // native <input> child is already in the tab order
                        // by default, so this was creating a redundant
                        // focusable stop that NVDA announced twice.
                        style: {
                            height: cmpType === "agentTool" ? "72px" : "48px",
                            border: dropdownCanShow ? "2px solid #3885E1" : theming === "dark" ? "2px solid #8D8D8D" : "1px solid #8D8D8D",
                            borderRadius: "42px",
                            boxShadow: "0px 2px 20px 0px rgba(0, 0, 0, 0.08)",
                            padding: "20px 44px 20px 12px",
                            ...( cmpType !== "agentTool" && {
                                padding: "20px 10px 20px 12px"
                            })
                        }
                    }}
                    inputProps={{
                        "aria-label": "Search Channels",
                        // [WAT-12043 / YFH-AMP113] The channel search is a
                        // combobox with a filterable listbox popup. Before
                        // this, screen readers announced it as a plain text
                        // field with no relationship to the suggestions
                        // below. The combobox pattern needs:
                        //   role="combobox"           on the input
                        //   aria-controls={listbox}   pointing at the popup <ul>
                        //   aria-expanded             reflects popup visibility
                        //   aria-autocomplete="list"  because suggestions change
                        //                             as the user types
                        // The listbox id is generated per-mount with useId
                        // (see `listboxId` above) and passed to DropdownList
                        // so two instances don't collide.
                        role: "combobox",
                        "aria-autocomplete": "list",
                        "aria-controls": listboxId,
                        "aria-expanded": showDrawer || showDrawerNoMatch,
                        style: {
                            ...INPUT_ELEMENT_STYLE,
                            ...(cmpType !== "agentTool" && {
                                padding: "26px 0px 18px 12px"
                            })
                        }
                    }}
                    sx={{
                        width: "100%",
                        backgroundColor: '#FFFFFF',
                        borderRadius: "42px",
                        cursor: "pointer",
                        // Mobile overrides (< 600px): 48px height, 16px font, 16px paddingLeft
                        [DtvTheme.breakpoints.down(DtvTheme.breakpoints.values.sm)]: {
                            "& .MuiOutlinedInput-root, & .DtvOutlinedInput-root": {
                                height: "48px !important",
                                padding: "10px 40px 10px 12px !important",
                                ...(cmpType !== "agentTool" && {
                                    padding: "10px 6px 10px 12px !important"
                                })
                            },
                            "& .MuiInputBase-input, & .MuiOutlinedInput-input": {
                                fontSize: "16px !important",
                                padding: "14px 0px 10px 16px !important",
                                ...(cmpType !== "agentTool" && {
                                    padding: "14px 0px 10px 6px !important"
                                })
                            }
                        },
                        "& .DtvFormLabel-root, & .MuiFormLabel-root, & .MuiInputLabel-root": {
                            cursor: "text",
                            color: isActive ? "#747474 !important" : "#555555 !important",
                            fontSize: isActive ? "14px !important"  : "20px !important",
                            fontFamily: "'Roboto', sans-serif !important",
                            fontWeight: "400 !important",
                            lineHeight: "1.3 !important",
                            transform: isActive ? "translate(54px, 4px) !important" : "translate(54px, 23px) !important",
                            ...(cmpType !== "agentTool" && {
                                fontSize: isActive ? "12px !important"  : "20px !important",
                                transform: isActive ? "translate(50px, 4px) !important" : "translate(50px, 12px) !important"
                            }),
                            maxWidth: "none !important",
                            // Generated by Copilot- Start - 2026-03-26 17:51:15
                            // Tablet: sm (600px) – 1199px only (between() ends at md - 0.05,
                            // and md is 1200 — the first desktop pixel)
                            [DtvTheme.breakpoints.between(DtvTheme.breakpoints.values.sm, DtvTheme.breakpoints.values.md)]: {
                                ...(isActive ? {
                                    fontSize: "14px !important",
                                    transform: "translate(54px, 4px) !important",
                                    ...(cmpType !== "agentTool" && {
                                        transform: "translate(48px, 4px) !important"
                                    }),
                                    lineHeight: "1 !important"
                                } : {
                                    fontSize: "20px !important",
                                    transform: "translate(54px, 23px) !important",
                                    ...(cmpType !== "agentTool" && {
                                        fontSize: "20px !important",
                                        transform: "translate(48px, 12px) !important"
                                    })
                                })
                            },
                            // Mobile: below sm (< 600px) only
                            [DtvTheme.breakpoints.down(DtvTheme.breakpoints.values.sm)]: {
                                ...(isActive ? {
                                    fontSize: "14px !important",
                                    transform: "translate(46px, 4px) !important",
                                    ...(cmpType !== "agentTool" && {
                                        transform: "translate(40px, 6px) scale(0.8) !important"
                                    }),
                                    lineHeight: "1 !important"
                                } : {
                                    fontSize: "14px !important",
                                    transform: "translate(50px, 15px) !important",
                                    ...(cmpType !== "agentTool" && {
                                        fontSize: cmpType === "interactiveTool" ? "14px !important" : "16px !important",
                                        transform: cmpType === "interactiveTool" ? "translate(40px, 16px) !important"  : "translate(40px, 14px) !important"
                                    })
                                })
                            }
                            // Generated by Copilot- End - 2026-03-26 17:51:15
                        },
                        "& .MuiOutlinedInput-root.Mui-focused, & .DtvOutlinedInput-root.Mui-focused": {
                            border: "2px solid #3885E1 !important"
                        },
                        "& fieldset, & .MuiOutlinedInput-notchedOutline, & .DtvOutlinedInput-notchedOutline": {
                            border: "none !important"
                        },
                        "& .MuiInputLabel-root.Mui-focused, & .DtvInputLabel-root.Dtv-focused": {
                            color: "#747474 !important",
                            border: "0"
                        },
                        "& .DtvOutlinedInput-root .DtvOutlinedInput-notchedOutline > legend, & .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline > legend, & .MuiOutlinedInput-root .DtvOutlinedInput-notchedOutline > legend": {
                            maxWidth: "0px",
                            width: "0px"
                        },
                        "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline > legend, & .DtvOutlinedInput-root.Dtv-focused .DtvOutlinedInput-notchedOutline > legend, & .MuiOutlinedInput-root.Mui-focused .DtvOutlinedInput-notchedOutline > legend": {
                            maxWidth: "0.01px",
                            width: "0px"
                        },
                        "& .DtvOutlinedInput-root.Mui-focused fieldset": {
                           border: "none"
                        },
                        ...(["chnlupModal", "agentTool"].includes(cmpType) && {
                        "& .DtvOutlinedInput-root, .MuiOutlinedInput-root": {
                            border: "2px solid #8D8D8D",
                            borderRadius: "42px",
                            boxShadow: "0px 2px 20px 0px rgba(0, 0, 0, 0.08)",
                            backgroundColor: "#FFFFFF",
                            outline: "none",
                            height: cmpType === "agentTool" ? '72px' : '48px'
                         },
                         "& .DtvOutlinedInput-root.Mui-focused, .MuiOutlinedInput-root.Mui-focused": {
                            border: "2px solid #3885E1 !important"
                         },
                        "& .DtvOutlinedInput-notchedOutline, .MuiOutlinedInput-notchedOutline": { border: "none" }
                    })
                    }}
                    onBlur={e => onTextFieldBlur(e)}
                    onClick={focusField}
                />

            </DtvBox>
            {/* Passes isLoading only in OV mode so the dropdown shows "Searching…"
                while the API call is in-flight instead of stale/empty results. */}
            {/* [WAT-12043 / YFH-AMP120] Screen-reader live region that
                announces the number of channel results as the user types.
                Always mounted so assistive tech is listening on page
                load; text is empty until results resolve, then flips to
                the CMS `typingMsg` at length=1, the CMS `noMatchListMsg`
                at 2+ chars with no matches, or "N results available"
                (falls back to English literals only if CMS is silent —
                see ternary below). aria-atomic ensures the whole updated
                phrase is re-announced. Positioned outside the visible
                layout via the shared @mui/utils `visuallyHidden` sx. */}
            <DtvBox
                aria-atomic="true"
                aria-live="polite"
                role="status"
                // [WAT-12043] MUI's canonical visuallyHidden (from
                // @mui/utils) instead of the hand-inlined 9-property sx
                // block that used to live here and in zipcode-module. One
                // source of truth for the sr-only style; the two live
                // regions can't drift apart on a copy edit.
                sx={visuallyHidden}
            >
                {/* Spoken text mirrors the visible message so the two never
                    drift or localize inconsistently. Uses the same
                    length-based ternary as DropdownList: at a single
                    typed character DropdownList shows the CMS `typingMsg`
                    ("keep typing…") rather than "no matches", so the
                    live region must too — announcing "no matches" while
                    the user has typed only one letter was the drift the
                    reviewer flagged. Falls back to English only if CMS
                    hasn't populated the strings. */}
                {showDrawer && effectiveFilteredData?.length > 0 ?
                    `${effectiveFilteredData.length} result${effectiveFilteredData.length === 1 ? "" : "s"} available` :
                    showDrawerNoMatch ?
                    ((deferredSearchText?.length === 1 ? effectiveCategoryData?.typingMsg : effectiveCategoryData?.noMatchListMsg) || "No results found") :
                    ""
                }
            </DtvBox>
            <MemoizedDropdownList
                categoryData={effectiveCategoryData}
                filteredData={effectiveFilteredData}
                isLoading={!defaultSortOption && isOVLoading}
                linkPosition={linkPosition}
                listboxId={listboxId}
                notifyOnCloseDropdown={handleCloseDropdown}
                notifyOnSelect={onSelectTag}
                notifyOnSelectAll={onMultiSelectTag}
                searchText={deferredSearchText}
                selectedChannels={selectedChannels}
                showDrawer={showDrawer}
                showDrawerNoMatch={showDrawerNoMatch}
                sortOptions={enableRadioButtonData ? defaultSortOption : true}
            />
             {/* Filter chips render the accumulated selected items below the input.
                 Only shown when showChips prop is true. */}
             {props?.config?.showChips && <MemoizedFilterChips handleRemoveTag={onSelectTag} linkPosition={linkPosition} selectedChannels={selectedChannels} theming={theming} />}
       </DtvBox>
  );
};
{ /* Generated by Copilot- End - 2026-02-23 16:38:41 */ }
